/**
 * CampaignSegmentation — shared, session-scoped channel × UTM × customer-type context.
 * Config comes from #campaign-segmentation-config (Campaign Segmentation header section).
 * UTMs prefer URLParametersTracking (utmData.js) when available.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'campaign-segmentation-config';
  var VISIT_KEY = 'mb_campaign_visit';
  var CUSTOMER_TYPE_KEY = 'mb_customer_type';
  var EVENT_NAME = 'campaign:segment-resolved';
  var PUBSUB_EVENT = 'campaign-segmentation:resolved';

  var state = {
    ready: false,
    config: null,
    segment: null,
    utm: {},
    customerType: 'new',
  };

  var listeners = [];

  function parseList(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(normalize).filter(Boolean);
    }
    return String(value)
      .split(',')
      .map(normalize)
      .filter(Boolean);
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .trim();
  }

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[CampaignSegmentation] Invalid config JSON', e);
      return null;
    }
  }

  function getUrlParam(key) {
    try {
      return new URLSearchParams(window.location.search).get(key);
    } catch (e) {
      return null;
    }
  }

  function getUtmContext() {
    var stored = {};
    if (window.URLParametersTracking && typeof window.URLParametersTracking.getStorage === 'function') {
      stored = window.URLParametersTracking.getStorage() || {};
    }

    // Current URL wins over stored attribution for fresh campaign entry.
    var utm_source = getUrlParam('utm_source') || stored.utm_source || '';
    var utm_medium = getUrlParam('utm_medium') || stored.utm_medium || '';
    var utm_campaign = getUrlParam('utm_campaign') || stored.utm_campaign || '';
    var utm_id = getUrlParam('utm_id') || stored.utm_id || '';
    var campaign_id =
      getUrlParam('campaign_id') ||
      getUrlParam('campaignid') ||
      getUrlParam('gad_campaignid') ||
      stored.campaign_id ||
      '';

    return {
      utm_source: normalize(utm_source),
      utm_medium: normalize(utm_medium),
      utm_campaign: normalize(utm_campaign),
      utm_id: normalize(utm_id),
      campaign_id: normalize(campaign_id),
    };
  }

  function detectCustomerType() {
    var override = normalize(getUrlParam('customer_type'));
    if (override === 'new' || override === 'returning') {
      try {
        localStorage.setItem(CUSTOMER_TYPE_KEY, override);
      } catch (e) {}
      return override;
    }

    try {
      var storedType = normalize(localStorage.getItem(CUSTOMER_TYPE_KEY));
      if (storedType === 'new' || storedType === 'returning') {
        return storedType;
      }
    } catch (e) {}

    // Visit heuristic: a prior visit timestamp older than this page load ⇒ returning.
    var now = Date.now();
    try {
      var prevVisit = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10);
      var isReturning = prevVisit > 0 && now - prevVisit > 30 * 60 * 1000;
      localStorage.setItem(VISIT_KEY, String(now));
      var type = isReturning ? 'returning' : 'new';
      localStorage.setItem(CUSTOMER_TYPE_KEY, type);
      return type;
    } catch (e) {
      return 'new';
    }
  }

  function markReturning() {
    try {
      localStorage.setItem(CUSTOMER_TYPE_KEY, 'returning');
    } catch (e) {}
    state.customerType = 'returning';
  }

  function fieldMatches(actual, ruleList, mode) {
    var rules = parseList(ruleList);
    if (!rules.length) return { matched: true, constrained: false };
    if (!actual) return { matched: false, constrained: true };

    var hit =
      mode === 'contains'
        ? rules.some(function (rule) {
            return actual.indexOf(rule) !== -1 || rule.indexOf(actual) !== -1;
          })
        : rules.indexOf(actual) !== -1;

    return { matched: hit, constrained: true };
  }

  function campaignIdCandidates(utm) {
    return [utm.campaign_id, utm.utm_id, utm.utm_campaign].filter(Boolean);
  }

  function scoreSegment(segment, utm, customerType) {
    var match = segment.match || {};
    var mode = match.campaign_match_mode === 'exact' ? 'exact' : 'contains';
    var score = 0;
    var checks = [];

    checks.push(fieldMatches(utm.utm_source, match.utm_source, 'exact'));
    checks.push(fieldMatches(utm.utm_medium, match.utm_medium, 'exact'));
    checks.push(fieldMatches(utm.utm_campaign, match.utm_campaign, mode));

    var campaignIdRules = parseList(match.campaign_id);
    if (campaignIdRules.length) {
      var candidates = campaignIdCandidates(utm);
      var idHit = candidates.some(function (candidate) {
        return fieldMatches(candidate, campaignIdRules, mode).matched;
      });
      // Campaign IDs are the most specific paid-social/PMax discriminator.
      checks.push({ matched: idHit, constrained: true, weight: 50 });

      // If this segment relies solely on campaign_id (no utm_source constraint)
      // but the visitor has a known utm_source from another channel, reject the match
      // to prevent cross-channel leakage (e.g. facebook visitor + Google PMAX ID).
      var sourceRules = parseList(match.utm_source);
      if (!sourceRules.length && utm.utm_source) {
        var otherChannelSources = ['facebook', 'instagram', 'fb', 'ig', 'meta', 'klaviyo', 'crm', 'influencer'];
        if (otherChannelSources.indexOf(utm.utm_source) !== -1) {
          checks.push({ matched: false, constrained: true });
        }
      }
    } else {
      checks.push({ matched: true, constrained: false });
    }

    var requiredType = normalize(match.customer_type || 'any');
    if (requiredType && requiredType !== 'any') {
      checks.push({ matched: requiredType === customerType, constrained: true });
    } else {
      checks.push({ matched: true, constrained: false });
    }

    for (var i = 0; i < checks.length; i++) {
      if (!checks[i].matched) return -1;
      if (checks[i].constrained) score += checks[i].weight || 10;
    }

    // Prefer more specific segments over loose defaults when both match.
    if (segment.isDefault) score -= 1;

    return score;
  }

  function resolveSegment(config, utm, customerType) {
    var segments = config.segments || [];
    var best = null;
    var bestScore = -1;
    var fallback = null;

    for (var i = 0; i < segments.length; i++) {
      var segment = segments[i];
      if (segment.isDefault && !fallback) fallback = segment;

      var score = scoreSegment(segment, utm, customerType);
      if (score > bestScore) {
        bestScore = score;
        best = segment;
      }
    }

    if (bestScore < 0) return fallback || segments[0] || null;
    return best;
  }

  function sessionStorageKey(campaignKey) {
    return 'campaignSegmentation:' + (campaignKey || 'default');
  }

  function readSession(campaignKey) {
    try {
      var raw = sessionStorage.getItem(sessionStorageKey(campaignKey));
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeSession(campaignKey, payload) {
    try {
      sessionStorage.setItem(sessionStorageKey(campaignKey), JSON.stringify(payload));
    } catch (e) {}
  }

  function fillSlots(segment) {
    if (!segment || !segment.content) return;
    var content = segment.content;

    document.querySelectorAll('[data-campaign-slot]').forEach(function (el) {
      // Codes-to-copy, product badge & selector tags have dedicated appliers.
      if (
        el.closest('[data-campaign-code]') ||
        el.closest('[data-campaign-product-badge]') ||
        el.hasAttribute('data-campaign-selector-tag')
      ) {
        return;
      }

      var slot = el.getAttribute('data-campaign-slot');
      var value = content[slot];
      if (value == null || value === '') return;

      if (el.tagName === 'IMG') {
        el.setAttribute('src', value);
        return;
      }

      if (el.hasAttribute('data-campaign-attr')) {
        el.setAttribute(el.getAttribute('data-campaign-attr'), value);
        return;
      }

      if (el.hasAttribute('data-campaign-html')) {
        el.innerHTML = value;
      } else {
        el.textContent = value;
      }

      el.hidden = false;
      el.classList.remove('hidden');
      el.removeAttribute('hidden');
    });
  }

  function applyCopyCodes(segment) {
    var content = (segment && segment.content) || {};
    var code = content.discount_code || '';
    var percent = content.discount_percent != null ? String(content.discount_percent) : '';
    var label = content.discount_label || '';
    var description = content.code_description || '';
    var hover = content.code_hover_text || 'Copied!';

    document.querySelectorAll('[data-campaign-code]').forEach(function (card) {
      var wrapper = card.closest('[data-campaign-copy-codes]');
      var hasStaticSibling = wrapper
        ? wrapper.querySelector('copy-codes:not([data-campaign-code])')
        : null;

      if (!code) {
        card.hidden = true;
        if (wrapper && !hasStaticSibling) wrapper.hidden = true;
        return;
      }

      card.dataset.code = code;
      card.setAttribute('data-code', code);
      card.dataset.discountPercent = percent;
      card.setAttribute('data-discount-percent', percent);

      var codeEl = card.querySelector('[data-campaign-slot="discount_code"]');
      if (codeEl) codeEl.textContent = code;

      var descEl = card.querySelector('[data-campaign-slot="code_description"]');
      if (descEl) descEl.innerHTML = description;

      var hoverEl = card.querySelector('[data-campaign-slot="code_hover_text"]');
      if (hoverEl) hoverEl.textContent = hover;

      var labelEl = card.querySelector('[data-campaign-slot="discount_label"]');
      if (labelEl) {
        var labelText = label;
        if (!labelText && percent && percent !== '0') {
          labelText = '-' + percent + '%';
        }
        if (descEl) {
          labelEl.textContent = labelText ? labelText + (description ? ' · ' : '') : '';
        } else {
          labelEl.textContent = labelText || '';
        }
      }

      card.hidden = false;
      card.removeAttribute('hidden');
      if (wrapper) {
        wrapper.hidden = false;
        wrapper.removeAttribute('hidden');
      }
    });
  }

  function parseBadgeLabel(text) {
    var raw = String(text || '').trim();
    if (!raw) return null;
    var lower = raw.toLowerCase();
    var match = raw.match(/(-?\s*\d+)\s*%/);
    var percent = match ? match[1].replace(/\s+/g, '') + '%' : raw;
    if (percent.charAt(0) !== '-' && /^\d/.test(percent)) percent = '-' + percent;
    var isBisZu = lower.indexOf('bis zu') !== -1;
    var isSparen = lower.indexOf('sparen') !== -1;
    return {
      label: raw,
      percent: percent,
      showUpTo: isBisZu,
      showSparen: isSparen && !isBisZu,
    };
  }

  function showProductBadgeElement(badge) {
    badge.hidden = false;
    badge.removeAttribute('hidden');
    badge.style.setProperty('display', 'flex', 'important');
    badge.style.setProperty('visibility', 'visible', 'important');
    badge.style.setProperty('opacity', '1', 'important');
    badge.style.setProperty('-webkit-transform', 'translateZ(0)');
    badge.style.setProperty('transform', 'translateZ(0)');
  }

  function findGalleryBadge(gallery) {
    if (!gallery) return null;
    var scoped = gallery.querySelector(':scope > [data-campaign-product-badge]');
    if (scoped) return scoped;
    return (
      Array.prototype.find.call(gallery.children, function (node) {
        return node.nodeType === 1 && node.hasAttribute('data-campaign-product-badge');
      }) || null
    );
  }

  function getGallerySlider(gallery) {
    var viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
    if (!viewer) return null;
    return viewer.querySelector('[id^="Slider-"]') || viewer.slider || null;
  }

  function getVisibleGallerySlides(gallery) {
    var slider = getGallerySlider(gallery);
    if (!slider) return [];
    return Array.prototype.filter.call(slider.querySelectorAll('.product__media-item'), function (item) {
      if (!item.getClientRects().length) return false;
      var style = window.getComputedStyle(item);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function isFirstSlideVisible(gallery) {
    var slider = getGallerySlider(gallery);
    var slides = getVisibleGallerySlides(gallery);
    if (!slider || !slides.length) return true;

    var first = slides[0];
    var sliderRect = slider.getBoundingClientRect();
    var slideRect = first.getBoundingClientRect();
    return Math.abs(slideRect.left - sliderRect.left) <= 20;
  }

  function markSingleImageGalleryBadges() {
    document.querySelectorAll('[data-campaign-product-badge]').forEach(function (badge) {
      var gallery =
        badge.closest('media-gallery, .product-gallery--single-image, .product__gallery-1-image') ||
        badge.closest('.product__media-wrapper');
      if (!gallery || gallery.classList.contains('product-gallery--single-image')) return;

      var mediaItems = gallery.querySelectorAll('.product__media-item');
      if (mediaItems.length === 1) {
        gallery.classList.add('product-gallery--single-image');
        return;
      }

      // 1-image layout: badge + lone preview image, no slider items.
      if (!mediaItems.length && gallery.querySelector('img')) {
        gallery.classList.add('product-gallery--single-image');
      }
    });
  }

  function isMobilePdpGallery() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function isMultiImagePdpGallery(gallery) {
    if (!gallery || gallery.tagName !== 'MEDIA-GALLERY') return false;
    if (gallery.classList.contains('product-gallery--single-image')) return false;
    return gallery.querySelectorAll('.product__media-item').length > 1;
  }

  function syncCampaignBadgeSlideVisibility(gallery, badge, onFirstSlide) {
    if (!badge || badge.hasAttribute('hidden')) {
      badge.classList.remove('campaign-product-badge--off-slide');
      return;
    }

    if (!isMobilePdpGallery() || !isMultiImagePdpGallery(gallery)) {
      badge.classList.remove('campaign-product-badge--off-slide');
      showProductBadgeElement(badge);
      return;
    }

    var show = onFirstSlide == null ? isFirstSlideVisible(gallery) : !!onFirstSlide;
    badge.classList.toggle('campaign-product-badge--off-slide', !show);
    if (show) {
      showProductBadgeElement(badge);
    } else {
      badge.style.setProperty('display', 'none', 'important');
      badge.style.setProperty('visibility', 'hidden', 'important');
      badge.style.setProperty('opacity', '0', 'important');
    }
  }

  function bindCampaignBadgeGallerySlides() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (gallery.dataset.campaignBadgeSlideBound === 'true') return;

      var badge = findGalleryBadge(gallery);
      if (!badge || badge.classList.contains('campaign-product-badge--card')) return;
      if (!isMultiImagePdpGallery(gallery)) return;

      gallery.dataset.campaignBadgeSlideBound = 'true';

      var update = function () {
        syncCampaignBadgeSlideVisibility(gallery, badge, null);
      };

      var attach = function attempt(tryCount) {
        var slider = getGallerySlider(gallery);
        var firstSlide = getVisibleGallerySlides(gallery)[0];

        if (!slider && (tryCount || 0) < 50) {
          window.setTimeout(function () {
            attempt((tryCount || 0) + 1);
          }, 100);
          return;
        }

        if (slider && slider.dataset.campaignBadgeScrollBound !== 'true') {
          slider.dataset.campaignBadgeScrollBound = 'true';
          slider.addEventListener(
            'scroll',
            function () {
              window.requestAnimationFrame(update);
            },
            { passive: true }
          );
        }

        var viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
        if (viewer && viewer.dataset.campaignBadgeSlideChangedBound !== 'true') {
          viewer.dataset.campaignBadgeSlideChangedBound = 'true';
          viewer.addEventListener('slideChanged', function () {
            update();
          });
        }

        if (
          firstSlide &&
          slider &&
          typeof IntersectionObserver !== 'undefined' &&
          !gallery._campaignBadgeSlideObserver
        ) {
          gallery._campaignBadgeSlideObserver = new IntersectionObserver(
            function (entries) {
              if (!isMobilePdpGallery()) return;
              var entry = entries[0];
              if (!entry) return;
              syncCampaignBadgeSlideVisibility(
                gallery,
                badge,
                entry.isIntersecting && entry.intersectionRatio > 0.25
              );
            },
            { root: slider, threshold: [0, 0.25, 0.5, 0.75] }
          );
          gallery._campaignBadgeSlideObserver.observe(firstSlide);
        }

        update();
      };

      attach(0);
    });
  }

  var campaignBadgeSlideResizeTimer;
  function bindCampaignBadgeSlideResize() {
    if (window._campaignBadgeSlideResizeBound) return;
    window._campaignBadgeSlideResizeBound = true;

    window.addEventListener('resize', function () {
      window.clearTimeout(campaignBadgeSlideResizeTimer);
      campaignBadgeSlideResizeTimer = window.setTimeout(function () {
        document.querySelectorAll('media-gallery[data-campaign-badge-slide-bound="true"]').forEach(function (gallery) {
          var badge = findGalleryBadge(gallery);
          if (!badge) return;
          syncCampaignBadgeSlideVisibility(gallery, badge, null);
        });
      }, 150);
    });
  }

  function applyProductBadge(segment) {
    var content = (segment && segment.content) || {};
    var text = content.product_badge_text || '';
    var parsed = parseBadgeLabel(text);

    document.querySelectorAll('[data-campaign-product-badge]').forEach(function (badge) {
      var slot = badge.querySelector('[data-campaign-slot="product_badge_text"]');
      var uptoEl = badge.querySelector('[data-campaign-badge-upto]');
      var percentEl = badge.querySelector('[data-campaign-badge-percent]');
      var suffixEl = badge.querySelector('[data-campaign-badge-suffix]');

      if (!parsed) {
        if (slot) slot.textContent = '';
        if (percentEl) percentEl.textContent = '';
        badge.hidden = true;
        badge.setAttribute('hidden', '');
        badge.style.removeProperty('display');
        badge.style.removeProperty('visibility');
        badge.style.removeProperty('opacity');
        badge.style.removeProperty('-webkit-transform');
        badge.style.removeProperty('transform');
        return;
      }

      if (slot) slot.textContent = parsed.label;

      if (badge.hasAttribute('data-campaign-card-tag')) {
        var tagTextEl = badge.querySelector('[data-campaign-card-tag-text]');
        if (tagTextEl) {
          tagTextEl.textContent = parsed.showUpTo ? 'Bis zu ' + parsed.percent : parsed.percent;
        }
        showProductBadgeElement(badge);
        return;
      }

      if (percentEl) percentEl.textContent = parsed.percent;
      if (uptoEl) {
        uptoEl.hidden = !parsed.showUpTo;
        if (parsed.showUpTo) uptoEl.removeAttribute('hidden');
      }
      if (suffixEl) {
        suffixEl.hidden = !parsed.showSparen;
        if (parsed.showSparen) suffixEl.removeAttribute('hidden');
      }

      showProductBadgeElement(badge);
    });

    markSingleImageGalleryBadges();
    bindCampaignBadgeGallerySlides();
    bindCampaignBadgeSlideResize();

    // Safari iOS can drop composited overlays until the next paint.
    window.requestAnimationFrame(function () {
      document.querySelectorAll('[data-campaign-product-badge]:not([hidden])').forEach(function (badgeEl) {
        if (badgeEl.classList.contains('campaign-product-badge--off-slide')) return;
        showProductBadgeElement(badgeEl);
      });
      document.querySelectorAll('media-gallery[data-campaign-badge-slide-bound="true"]').forEach(function (gallery) {
        var badgeEl = findGalleryBadge(gallery);
        if (badgeEl) syncCampaignBadgeSlideVisibility(gallery, badgeEl, null);
      });
    });
  }

  function normalizeSelectorTagText(text, percent) {
    if (percent != null && percent !== '' && Number(percent) > 0) {
      return '-' + Math.round(Number(percent)) + '%';
    }
    if (!text) return '';
    var match = String(text).match(/(\d+)\s*%/);
    if (match) return '-' + match[1] + '%';
    return String(text).replace(/bis zu\s*/gi, '').replace(/up to\s*/gi, '').trim();
  }

  function applySelectorTags(segment) {
    var content = (segment && segment.content) || {};
    var mode = String(content.birthday_card_mode || '').toLowerCase();
    var key = String((segment && segment.key) || '').toLowerCase();
    var infoMode = mode === 'info' || mode === 'popup' || key === 'influencer';

    var text = normalizeSelectorTagText(content.selector_tag_text, content.selector_tag_percent);

    document.querySelectorAll('[data-campaign-selector-tag]').forEach(function (tag) {
      if (infoMode || !text) {
        tag.textContent = '';
        tag.classList.remove('is-birthday-active');
        tag.hidden = true;
        tag.setAttribute('hidden', '');
        tag.style.setProperty('display', 'none', 'important');
        return;
      }

      tag.textContent = text;
      tag.hidden = false;
      tag.removeAttribute('hidden');
      tag.style.removeProperty('display');
    });
  }

  function applyAnnouncement(config, segment) {
    if (!config.overrideAnnouncementBar) return;
    var text = segment && segment.content && segment.content.announcement_text;
    if (!text) return;

    var slots = document.querySelectorAll(
      '[data-campaign-slot="announcement_text"], .announcement-bar__message:not(.announcement-bar__trustpilot-text) > span'
    );
    slots.forEach(function (slot) {
      slot.innerHTML = text;
      slot.hidden = false;
    });

    var bar = document.querySelector('.announcement-bar');
    if (bar) {
      bar.style.display = '';
      if (segment.content.announcement_inverse) {
        bar.classList.add('color-inverse');
      }
    }

    var link = segment.content.announcement_link;
    if (link) {
      document.querySelectorAll('.announcement-bar__link').forEach(function (anchor) {
        anchor.setAttribute('href', link);
      });
    }
  }

  function setDocumentHints(segment, customerType, utm) {
    var root = document.documentElement;
    if (!segment) return;
    root.dataset.campaignSegment = segment.key || '';
    root.dataset.campaignCustomerType = customerType || '';
    if (utm.utm_source) root.dataset.campaignUtmSource = utm.utm_source;
  }

  function notify(payload) {
    listeners.forEach(function (cb) {
      try {
        cb(payload);
      } catch (e) {
        console.warn('[CampaignSegmentation] listener error', e);
      }
    });

    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: payload,
      })
    );

    if (typeof publish === 'function') {
      publish(PUBSUB_EVENT, payload);
    }
  }

  function buildPayload(segment, utm, customerType, fromCache) {
    return {
      segment: segment,
      content: (segment && segment.content) || {},
      key: (segment && segment.key) || null,
      label: (segment && segment.label) || null,
      utm: utm,
      customerType: customerType,
      fromCache: !!fromCache,
    };
  }

  function init() {
    var config = readConfig();
    if (!config || !config.enabled) return;

    state.config = config;
    state.utm = getUtmContext();
    state.customerType = detectCustomerType();

    // QA / debug: ?campaign_refresh=1 forces a rematch (clears session lock).
    // Without this, the first resolved segment sticks for the whole tab session
    // (intentional — see SESS-01/02 in the announcement-bar QA matrix).
    var forceRefresh =
      normalize(getUrlParam('campaign_refresh')) === '1' ||
      normalize(getUrlParam('campaign_segment_reset')) === '1';
    if (forceRefresh) {
      try {
        sessionStorage.removeItem(sessionStorageKey(config.campaignKey));
      } catch (e) {}
    }

    var cached = readSession(config.campaignKey);
    if (cached && cached.segment && !forceRefresh) {
      state.segment = cached.segment;
      state.ready = true;
      setDocumentHints(state.segment, cached.customerType || state.customerType, cached.utm || state.utm);
      fillSlots(state.segment);
      applyCopyCodes(state.segment);
      applyProductBadge(state.segment);
      applySelectorTags(state.segment);
      applyAnnouncement(config, state.segment);
      applyCampaignCardPrices();
      notify(buildPayload(state.segment, cached.utm || state.utm, cached.customerType || state.customerType, true));
      return;
    }

    var segment = resolveSegment(config, state.utm, state.customerType);
    state.segment = segment;
    state.ready = true;

    writeSession(config.campaignKey, {
      segment: segment,
      utm: state.utm,
      customerType: state.customerType,
      resolvedAt: Date.now(),
    });

    setDocumentHints(segment, state.customerType, state.utm);
    fillSlots(segment);
    applyCopyCodes(segment);
    applyProductBadge(segment);
    applySelectorTags(segment);
    applyAnnouncement(config, segment);
    applyCampaignCardPrices();
    notify(buildPayload(segment, state.utm, state.customerType, false));
  }

  // --- Visual price sync (codes-to-copy → selector totals + CTA) ---

  var APPLIED_KEY = 'campaignSegmentation:appliedDiscount';
  var priceSyncState = {
    percent: 0,
    code: '',
  };

  function parseMoney(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && !isNaN(value)) return value;

    var cleaned = String(value).replace(/[^\d,.-]/g, '');
    if (!cleaned) return null;

    if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') > -1) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.indexOf(',') > -1) {
      cleaned = cleaned.replace(',', '.');
    }

    var amount = parseFloat(cleaned);
    return isNaN(amount) ? null : amount;
  }

  function formatMoney(amount) {
    if (amount == null || isNaN(amount)) return '';
    return Number(amount).toFixed(2) + ' €';
  }

  function eurosToCents(amount) {
    if (amount == null || isNaN(amount)) return 0;
    return Math.round(Number(amount) * 100);
  }

  function centsToEuros(cents) {
    return Number(cents) / 100;
  }

  /** Match Shopify line-item % discounts: price − floor(price × percent / 100). */
  function discountCents(baseCents, percent) {
    baseCents = parseInt(baseCents, 10) || 0;
    percent = parseFloat(percent) || 0;
    if (!baseCents || !percent) return baseCents;
    var offCents = Math.floor((baseCents * percent) / 100);
    return baseCents - offCents;
  }

  function readPerDayLabelTemplate() {
    if (state.config && state.config.perDayLabelTemplate) {
      return state.config.perDayLabelTemplate;
    }
    return 'ab __PRICE__/Tag';
  }

  function formatPerDayLabel(cents) {
    var euros = cents / 100;
    return readPerDayLabelTemplate().replace('__PRICE__', formatMoney(euros));
  }

  function isInfoCardMode(content) {
    content = content || (state.segment && state.segment.content) || {};
    if (window.BirthdaySalePDP && typeof window.BirthdaySalePDP.isInfoMode === 'function') {
      return window.BirthdaySalePDP.isInfoMode(content);
    }
    var mode = String(content.birthday_card_mode || '').toLowerCase();
    return mode === 'info' || mode === 'popup';
  }

  function segmentHasCampaignDiscount(content) {
    content = content || {};
    if (parseFloat(content.discount_percent) > 0) return true;
    if (content.discount_mode === 'tiered') {
      var low = parseFloat(content.tier_percent_low);
      var high = parseFloat(content.tier_percent_high);
      return (!isNaN(low) && low > 0) || (!isNaN(high) && high > 0);
    }
    return false;
  }

  function shouldApplyCardCampaignPrices(content) {
    content = content || (state.segment && state.segment.content) || {};
    if (!state.config || !state.config.enabled) return false;
    if (segmentHasCampaignDiscount(content)) return true;
    if (priceSyncState.percent > 0) return true;
    return false;
  }

  function resolveCardCampaignPercent(content) {
    content = content || (state.segment && state.segment.content) || {};
    if (!shouldApplyCardCampaignPrices(content)) return 0;

    if (priceSyncState.percent > 0) return priceSyncState.percent;

    if (content.discount_mode === 'tiered') {
      var tierHigh = parseFloat(content.tier_percent_high);
      if (!isNaN(tierHigh) && tierHigh > 0) return tierHigh;
      return parseFloat(content.tier_percent_low) || 0;
    }

    return parseFloat(content.discount_percent) || 0;
  }

  function applyCampaignCardPrices() {
    var content = (state.segment && state.segment.content) || {};
    var percent = resolveCardCampaignPercent(content);

    document.querySelectorAll('[data-campaign-card-prices]').forEach(function (wrap) {
      var baseCents = parseInt(wrap.dataset.campaignPerDayCents, 10);
      if (!baseCents || baseCents <= 0) return;

      var defaultEl = wrap.querySelector('[data-campaign-card-price-default]');
      var comparisonEl = wrap.querySelector('[data-campaign-card-price-comparison]');
      var saleEl = wrap.querySelector('[data-campaign-card-price-sale]');
      var strikeEl = wrap.querySelector('[data-campaign-card-price-strike]');

      if (!percent || percent <= 0) {
        if (comparisonEl) comparisonEl.hidden = true;
        if (defaultEl) {
          defaultEl.hidden = false;
          defaultEl.removeAttribute('hidden');
        }
        return;
      }

      var saleCents = discountCents(baseCents, percent);
      if (saleCents >= baseCents) {
        if (comparisonEl) comparisonEl.hidden = true;
        if (defaultEl) {
          defaultEl.hidden = false;
          defaultEl.removeAttribute('hidden');
        }
        return;
      }

      if (defaultEl) defaultEl.hidden = true;
      if (comparisonEl) {
        comparisonEl.hidden = false;
        comparisonEl.removeAttribute('hidden');
      }
      if (saleEl) saleEl.textContent = formatPerDayLabel(saleCents);
      if (strikeEl) {
        strikeEl.textContent = formatPerDayLabel(baseCents);
        strikeEl.hidden = false;
        strikeEl.removeAttribute('hidden');
      }
    });
  }

  function readAppliedDiscount() {
    try {
      var raw = sessionStorage.getItem(APPLIED_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeAppliedDiscount(payload) {
    try {
      sessionStorage.setItem(APPLIED_KEY, JSON.stringify(payload));
    } catch (e) {}
  }

  function optionPriceNodes() {
    return document.querySelectorAll(
      '.v2-option-pill[data-price-default], .dropdown-option[data-price-default], [data-campaign-price-sync][data-price-default]'
    );
  }

  function applyDiscountToOptionNodes(percent) {
    optionPriceNodes().forEach(function (el) {
      if (!el.dataset.campaignBaseTotal) {
        el.dataset.campaignBaseTotal = el.dataset.priceDefault || '';
        el.dataset.campaignBaseStrike = el.dataset.strikePrice || '';
      }

      var base = parseMoney(el.dataset.campaignBaseTotal);
      if (base == null) return;

      var baseCents = eurosToCents(base);
      var discountedCents = discountCents(baseCents, percent);
      el.dataset.priceDefault = formatMoney(centsToEuros(discountedCents));

      var existingStrike = parseMoney(el.dataset.campaignBaseStrike);
      var content = (state.segment && state.segment.content) || {};
      if (content.enable_strike_price === false) {
        el.dataset.strikePrice = '';
      } else {
        var strikeAmount = existingStrike && existingStrike > base ? existingStrike : base;
        el.dataset.strikePrice = formatMoney(strikeAmount);
      }
    });
  }

  function refreshActiveInfoBars() {
    ['sub', 'ot'].forEach(function (prefix) {
      var totalEl = document.querySelector('#item_' + prefix + '_total');
      var strikeEl = document.querySelector('#item_' + prefix + '_strike_price');
      if (!totalEl) return;

      var panel = totalEl.closest('.v2-panel') || totalEl.closest('[data-panel]') || document;
      var active =
        panel.querySelector('.v2-option-pill.active') ||
        panel.querySelector('.dropdown-option.selected') ||
        panel.querySelector('.v2-option-pill') ||
        panel.querySelector('.dropdown-option');

      if (!active || !active.dataset.priceDefault) return;

      var totalLabel = totalEl.dataset.totalLabel || '';
      totalEl.textContent = totalLabel
        ? active.dataset.priceDefault + '/' + totalLabel
        : active.dataset.priceDefault;

      if (strikeEl) {
        if (active.dataset.strikePrice) {
          strikeEl.textContent = active.dataset.strikePrice;
          strikeEl.style.display = '';
        } else {
          strikeEl.style.display = 'none';
        }
      }
    });
  }

  function findAlpinePriceScope() {
    var nodes = document.querySelectorAll('.product__buy-buttons [x-data], product-form [x-data]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el._x_dataStack && el._x_dataStack[0] && typeof el._x_dataStack[0].price !== 'undefined') {
        return el._x_dataStack[0];
      }
    }
    return null;
  }

  function readActivePickerDisplayed() {
    if (window.BirthdaySalePDP && typeof window.BirthdaySalePDP.readPickerTotals === 'function') {
      var fromBirthday = window.BirthdaySalePDP.readPickerTotals();
      if (fromBirthday && fromBirthday.sale != null && fromBirthday.sale > 0) {
        return fromBirthday;
      }
    }

    var active =
      document.querySelector('.v2-option-pill.active[data-price-default]') ||
      document.querySelector('.dropdown-option.selected[data-price-default]') ||
      document.querySelector('.product-options_package input[type="radio"]:checked');
    var optionInfo = null;
    if (active && active.matches && active.matches('input[type="radio"]')) {
      var label =
        (active.id && document.querySelector('label[for="' + active.id + '"]')) ||
        active.closest('label');
      optionInfo = label && label.querySelector('.option__info:not(.hidden)');
      active = optionInfo || active;
    }
    if (!active) return null;

    if (active.classList && active.classList.contains('option__info')) {
      var totalNode = active.querySelectorAll('[itemprop="price"]');
      var last = totalNode.length ? totalNode[totalNode.length - 1] : null;
      var sale = null;
      if (last) {
        var cents = parseInt(last.getAttribute('content'), 10);
        if (!isNaN(cents) && cents > 0) sale = cents / 100;
        if (sale == null) sale = parseMoney(String(last.textContent).split('/')[0]);
      }
      if (sale == null) return null;
      var strike =
        parseMoney(active.dataset.strikePrice) ||
        parseMoney(active.dataset.birthdayBaseTotal) ||
        sale;
      return { sale: sale, strike: strike > sale ? strike : sale };
    }

    if (!active.dataset || !active.dataset.priceDefault) return null;
    var saleFromData = parseMoney(active.dataset.priceDefault);
    if (saleFromData == null) return null;
    var strikeFromData =
      parseMoney(active.dataset.strikePrice) ||
      parseMoney(active.dataset.campaignBaseTotal) ||
      saleFromData;
    return { sale: saleFromData, strike: strikeFromData > saleFromData ? strikeFromData : saleFromData };
  }

  function applyDiscountToCta(percent) {
    if (window.BirthdaySalePDP && typeof window.BirthdaySalePDP.isActive === 'function' && window.BirthdaySalePDP.isActive()) {
      return;
    }

    var picker = readActivePickerDisplayed();
    var alpine = findAlpinePriceScope();

    if (picker) {
      var saleCents = eurosToCents(picker.sale);
      var strikeCents = eurosToCents(picker.strike);
      if (alpine) {
        alpine.price = saleCents;
        alpine.comparePrice = strikeCents > saleCents ? strikeCents : 0;
      }
      document.querySelectorAll('.atc-price_main').forEach(function (el) {
        if (!el.dataset) return;
        if (el.dataset.subscriptionPrice) el.dataset.subscriptionPrice = formatMoney(picker.sale);
        if (el.dataset.onetimerPrice) el.dataset.onetimerPrice = formatMoney(picker.sale);
      });
      var productForm = document.querySelector('product-form');
      if (productForm && typeof productForm.togglePrices === 'function') {
        productForm.togglePrices();
      }
      return;
    }

    if (alpine) {
      if (alpine._campaignBasePrice == null) {
        alpine._campaignBasePrice = alpine.price;
        alpine._campaignBaseCompare = alpine.comparePrice || 0;
      }
      var baseCents = parseInt(alpine._campaignBasePrice, 10) || 0;
      var compareBase = parseInt(alpine._campaignBaseCompare, 10) || 0;
      alpine.comparePrice = Math.max(compareBase, baseCents);
      alpine.price = discountCents(baseCents, percent);
    }

    document.querySelectorAll('.atc-price_main[data-subscription-price], .atc-price_main[data-onetimer-price]').forEach(function (el) {
      ['subscriptionPrice', 'onetimerPrice'].forEach(function (key) {
        var attr = key === 'subscriptionPrice' ? 'subscriptionPrice' : 'onetimerPrice';
        var raw = el.dataset[attr];
        if (!raw) return;
        if (!el.dataset['campaignBase' + attr]) {
          el.dataset['campaignBase' + attr] = raw;
        }
        var storedBaseCents = eurosToCents(parseMoney(el.dataset['campaignBase' + attr]));
        if (!storedBaseCents) return;
        el.dataset[attr] = formatMoney(centsToEuros(discountCents(storedBaseCents, percent)));
      });
    });

    var productForm = document.querySelector('product-form');
    if (productForm && typeof productForm.togglePrices === 'function') {
      productForm.togglePrices();
    }
  }

  function applyVisualPriceDiscount(percent, code) {
    percent = parseFloat(percent);
    if (!percent || percent <= 0) return;

    priceSyncState.percent = percent;
    priceSyncState.code = code || '';
    writeAppliedDiscount({ percent: percent, code: priceSyncState.code, at: Date.now() });

    applyDiscountToOptionNodes(percent);
    refreshActiveInfoBars();
    applyDiscountToCta(percent);
    applyCampaignCardPrices();

    document.documentElement.dataset.campaignDiscountPercent = String(percent);
    document.dispatchEvent(
      new CustomEvent('campaign:prices-updated', {
        detail: { percent: percent, code: priceSyncState.code },
      })
    );
  }

  function reapplyVisualPriceDiscount() {
    if (priceSyncState.percent > 0) {
      applyDiscountToOptionNodes(priceSyncState.percent);
      refreshActiveInfoBars();
      applyDiscountToCta(priceSyncState.percent);
    }
  }

  function bindPriceSync() {
    document.addEventListener('campaign:code-applied', function (event) {
      var detail = event.detail || {};
      var percent = detail.percent;
      if (percent == null || percent === '') {
        var content = api.getContent();
        percent = content.discount_percent;
      }
      applyVisualPriceDiscount(percent, detail.code);
    });

    // Keep discounted totals after picker / variant UI refreshes.
    document.addEventListener('click', function (event) {
      if (!priceSyncState.percent) return;
      if (window.BirthdaySalePDP && typeof window.BirthdaySalePDP.isActive === 'function' && window.BirthdaySalePDP.isActive()) {
        return;
      }
      if (
        event.target.closest('.v2-option-pill') ||
        event.target.closest('.dropdown-option') ||
        event.target.closest('.option__info') ||
        event.target.closest('.product-options_package label') ||
        event.target.closest('.v2-toggle-btn') ||
        event.target.closest('.plan-button')
      ) {
        window.setTimeout(reapplyVisualPriceDiscount, 0);
      }
    });

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      try {
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () {
          window.setTimeout(reapplyVisualPriceDiscount, 50);
        });
      } catch (e) {}
    }

    var applied = readAppliedDiscount();
    if (applied && applied.percent > 0 && !document.querySelector('birthday-sale-card')) {
      priceSyncState.percent = applied.percent;
      priceSyncState.code = applied.code || '';
      // Wait a tick so picker pills exist.
      window.setTimeout(function () {
        applyVisualPriceDiscount(applied.percent, applied.code);
      }, 100);
    }
  }

  var api = {
    EVENT: EVENT_NAME,
    PUBSUB_EVENT: PUBSUB_EVENT,
    isReady: function () {
      return state.ready;
    },
    getConfig: function () {
      return state.config;
    },
    getSegment: function () {
      return state.segment;
    },
    getContent: function () {
      return (state.segment && state.segment.content) || {};
    },
    getCustomerType: function () {
      return state.customerType;
    },
    getUtm: function () {
      return state.utm;
    },
    get: function () {
      return buildPayload(state.segment, state.utm, state.customerType, false);
    },
    onChange: function (callback) {
      if (typeof callback !== 'function') return function () {};
      listeners.push(callback);
      if (state.ready) callback(api.get());
      return function unsubscribe() {
        listeners = listeners.filter(function (cb) {
          return cb !== callback;
        });
      };
    },
    markReturning: markReturning,
    applyVisualPriceDiscount: applyVisualPriceDiscount,
    applyCampaignCardPrices: applyCampaignCardPrices,
    getAppliedDiscount: function () {
      return { percent: priceSyncState.percent, code: priceSyncState.code };
    },
    /**
     * Re-fill product badges / selector tags after AJAX grid swaps
     * (collection filter tabs, section re-renders, etc.).
     */
    reapplyProductSurfaces: function () {
      if (!state.ready || !state.segment) return;
      applyProductBadge(state.segment);
      applySelectorTags(state.segment);
      applyCampaignCardPrices();
      if (window.BirthdaySalePDP && typeof window.BirthdaySalePDP.syncAll === 'function') {
        window.BirthdaySalePDP.syncAll();
      }
    },
    /** Force re-resolve (e.g. theme editor preview). Clears session lock. */
    refresh: function () {
      if (state.config) {
        try {
          sessionStorage.removeItem(sessionStorageKey(state.config.campaignKey));
        } catch (e) {}
      }
      init();
    },
  };

  window.CampaignSegmentation = api;

  function stickyHeaderOffset() {
    var header =
      document.querySelector('sticky-header') ||
      document.querySelector('#shopify-section-header') ||
      document.querySelector('.header-wrapper');
    if (!header) return 0;
    var h = header.getBoundingClientRect().height;
    return h > 0 ? Math.ceil(h) : 0;
  }

  function shouldScrollToProducts() {
    try {
      if (sessionStorage.getItem('campaignScrollToProducts') === '1') return true;
    } catch (e) {}
    try {
      return new URLSearchParams(window.location.search).get('campaign_scroll') === '1';
    } catch (err) {
      return false;
    }
  }

  function clearScrollToProductsFlag() {
    try {
      sessionStorage.removeItem('campaignScrollToProducts');
    } catch (e) {}

    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('campaign_scroll')) return;
      url.searchParams.delete('campaign_scroll');
      var next = url.pathname + url.search + url.hash;
      window.history.replaceState({}, '', next);
    } catch (err) {}
  }

  function findProductsScrollTarget() {
    return (
      document.getElementById('ProductGridContainer') ||
      document.getElementById('product-grid') ||
      document.querySelector('.product-grid-container') ||
      document.querySelector('#shopify-section-product-grid') ||
      document.querySelector('.collection')
    );
  }

  function scrollToCollectionProducts(options) {
    options = options || {};
    if (!options.force && !shouldScrollToProducts()) return false;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var target = findProductsScrollTarget();
    if (!target && !options.allowFallback) return false;

    var headerOffset = stickyHeaderOffset() + (isMobile ? 12 : 20);
    var y;

    if (target) {
      y = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    } else {
      // Fallback only after retries exhausted — keep below the tall campaign hero.
      y = isMobile ? Math.round(window.innerHeight * 0.85) : 720;
    }

    y = Math.max(0, Math.round(y));

    window.requestAnimationFrame(function () {
      window.scrollTo({ top: y, behavior: options.instant ? 'auto' : 'smooth' });
    });

    if (!options.keepFlag) clearScrollToProductsFlag();
    return true;
  }

  /**
   * Retry scroll until the product grid exists / layout settles.
   * Filters and late section renders often shift the grid after first paint.
   */
  function scheduleScrollToCollectionProducts() {
    if (!shouldScrollToProducts()) return;

    var delays = [100, 350, 700, 1200, 2000];
    var completed = false;

    delays.forEach(function (delay, index) {
      window.setTimeout(function () {
        if (completed) return;
        var isLast = index === delays.length - 1;
        var ok = scrollToCollectionProducts({
          allowFallback: isLast,
          keepFlag: !isLast,
          force: true,
        });
        if (ok && findProductsScrollTarget()) {
          completed = true;
          clearScrollToProductsFlag();
        } else if (isLast) {
          clearScrollToProductsFlag();
        }
      }, delay);
    });
  }

  // Expose for hero CTA same-page clicks.
  api.scrollToCollectionProducts = function () {
    try {
      sessionStorage.setItem('campaignScrollToProducts', '1');
    } catch (e) {}
    scheduleScrollToCollectionProducts();
  };

  function boot() {
    init();
    bindPriceSync();
    scheduleScrollToCollectionProducts();

    // Collection filter tabs replace #product-grid via Section Rendering API;
    // badges come back with [hidden] and need the segment copy re-applied.
    document.addEventListener('collection:products-updated', function () {
      api.reapplyProductSurfaces();
      if (shouldScrollToProducts()) scheduleScrollToCollectionProducts();
    });
    document.addEventListener('collection-tab-changed', function () {
      api.reapplyProductSurfaces();
    });

    document.addEventListener('birthday-sale:updated', function () {
      applyCampaignCardPrices();
    });

    document.addEventListener('campaign:segment-resolved', function () {
      window.requestAnimationFrame(applyCampaignCardPrices);
      window.setTimeout(applyCampaignCardPrices, 150);
    });

    window.addEventListener('pageshow', function () {
      window.requestAnimationFrame(function () {
        api.reapplyProductSurfaces();
      });
      // bfcache restores can keep the scroll flag — honour it.
      scheduleScrollToCollectionProducts();
    });

    // Safari iOS: gallery custom elements may connect after first segment pass.
    window.setTimeout(function () {
      if (state.ready) api.reapplyProductSurfaces();
    }, 350);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
